import type { CampaignSheetMode } from '@/@types/api-v1/campaign'
import { StampComponents } from '@/components/Pages/home/components/StampComponents'
import { Typography } from '@/components/UI/Typography'
import styles from '@/styles/Home.module.css'
import { IconLock } from '@tabler/icons-react'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface StampInSheet {
  id: number
  name: string
  spotId: number
  spots: Spot[]
  period: boolean
  point: number
  image: string
  overlayImage?: string | null
  hasStamped?: boolean
  isHas?: boolean
  hasCount?: number
  stampCount?: number
  earnedPoints?: number
}

interface UserSheetObject {
  id: number
  name: string
  description: string
  earnedPoints: number
  startAt: string
  endAt: string
  total: number
  hasCount: number
  backgroundImage: string
  stamps: StampInSheet[]
}

interface Spot {
  address?: string
  id: number
  name: string
  zip?: string
}

interface IStampSheetImageSize {
  width: number
  height: number
  index: number
}

export interface StampSheetStampInfo {
  id: number
  name: string
  image: string | null
  point: number
  isHas: boolean
  spots: Spot[]
}

interface StampSheetProps {
  idx: number
  sheet: UserSheetObject
  campaign: {
    isRepeatStamp: boolean
    stampShape: number
    sheetMode: CampaignSheetMode
  }
  /** シートが未開放（startAt が未来）かどうか */
  isLocked?: boolean
  /** シートの開放日時（ISO8601）。isLocked=true のときに表示する */
  startAt?: string | null
  /** スタンプをタップした時に呼ばれる。ダイアログ表示に使用する */
  onStampClick: (stamp: StampSheetStampInfo) => void
  // sheetMode === "OVERLAY" の場合は、画像のサイズを取得するために必要
  setStampSheetImageSize: React.Dispatch<React.SetStateAction<IStampSheetImageSize[]>>
}

/**
 * campaign.type === 1の、通常のスタンプの場合のシート
 * */
export const StampSheet = ({
  idx,
  sheet,
  campaign,
  isLocked = false,
  startAt,
  onStampClick,
  setStampSheetImageSize,
}: StampSheetProps) => {
  const { t } = useTranslation()
  const [imageSize, setImageSize] = useState<{
    natural: { width: number; height: number } | null
    rendered: { width: number; height: number } | null
    fixed: { width: number; height: number } | null
  }>({ natural: null, rendered: null, fixed: { width: 100, height: 100 } })

  const [screenSize, setScreenSize] = useState<{
    width: number
    height: number
  }>({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  })

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }

    // 初期サイズを設定
    handleResize()

    // リサイズイベントリスナーを追加
    window.addEventListener('resize', handleResize)

    // クリーンアップ関数
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget
    const naturalSize = {
      width: img.naturalWidth,
      height: img.naturalHeight,
    }
    const renderedSize = {
      width: img.clientWidth,
      height: img.clientHeight,
    }
    const fixedWidth = (screenSize.width * 11) / 12 // 画面幅から余白を引いた高さ
    const fixedHeight = Math.floor((fixedWidth * (img.naturalHeight || 1)) / (img.naturalWidth || 1))

    setImageSize({
      natural: naturalSize,
      rendered: renderedSize,
      fixed: { width: fixedWidth, height: fixedHeight },
    })
    // 40pxはmtとして追加されている分
    setStampSheetImageSize((prev) => {
      const newEntry = { width: fixedWidth, height: fixedHeight + 40, index: idx }
      const found = prev.some((item) => item.index === idx)
      if (found) {
        return prev.map((item) => (item.index === idx ? newEntry : item))
      } else {
        return [...prev, newEntry]
      }
    })
  }
  if (campaign.sheetMode === 'NORMAL' || campaign.sheetMode === 'BACKGROUND') {
    // それぞれの場合に応じたstyleを設定
    const style =
      campaign.sheetMode === 'NORMAL'
        ? { backgroundColor: '#ffffff' }
        : campaign.sheetMode === 'BACKGROUND'
          ? {
              backgroundImage: `url("${sheet.backgroundImage}")`,
              backgroundRepeat: 'repeat-y',
              backgroundSize: '100% auto',
            }
          : {}
    return (
      <div
        key={`div-${sheet.id}`}
        className="mt-4 rounded-lg pt-6 border border-gray-200 relative overflow-hidden"
        style={style}
      >
        {/* ロックオーバーレイ */}
        {isLocked && (
          <div className="absolute inset-0 z-10 bg-black/40 flex flex-col items-center justify-center rounded-lg pointer-events-none">
            <IconLock size={48} className="text-white" aria-hidden="true" />
            {startAt && (
              <p className="mt-2 text-white text-xs font-bold text-center leading-relaxed">
                {new Date(startAt).toLocaleDateString('ja-JP', {
                  month: 'numeric',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}{' '}
                開放
              </p>
            )}
          </div>
        )}
        <div className="relative">
          <Typography variant="body1" component="h2" className="px-3 text-center">
            {sheet.name}
          </Typography>
        </div>
        <div className="mt-1 mr-3 flex items-center justify-end">
          <Typography variant="body2">
            {campaign.isRepeatStamp
              ? `${t('home.you_got', '獲得数')}： ${sheet.hasCount}${t('home.count_unit', '個')}`
              : `${t('home.achievement_count', '達成状況')}： ${sheet.hasCount} / ${sheet.total}`}
          </Typography>
        </div>
        <div className={`grid grid-cols-3 items-start justify-items-center ${styles.c_stamp_contents_wrap}`}>
          {sheet.stamps?.map((contentStamp, i: number) => {
            const stampNotAcquired = contentStamp.image.endsWith('system/non-stamp.png')
            const stampShape = stampNotAcquired ? 'circular' : campaign.stampShape === 0 ? 'circular' : 'square'
            return (
              <StampComponents
                key={`slide-${sheet.id}-stamp-cmp-${contentStamp.spotId}-${i}`}
                query={contentStamp.spotId}
                hasCount={contentStamp.hasCount ?? 0}
                course={sheet.name}
                img={contentStamp.image}
                name={contentStamp.name}
                spots={contentStamp.spots as [{ address: string; id: number; name: string; zip: string }]}
                isRepeatStamp={campaign?.isRepeatStamp}
                stampShape={stampShape}
                onGroupModal={() =>
                  onStampClick({
                    id: contentStamp.id,
                    name: contentStamp.name,
                    image: contentStamp.image,
                    point: contentStamp.point,
                    isHas: contentStamp.isHas ?? false,
                    spots: contentStamp.spots,
                  })
                }
              />
            )
          })}
        </div>
      </div>
    )
  }
  if (campaign.sheetMode === 'OVERLAY') {
    const fixedSize = imageSize.fixed ?? { width: 100, height: 100 }
    return (
      <div className="mt-4 rounded-lg pt-6 border border-gray-200 relative overflow-hidden">
        {/* オーバーレイ画像エリア：背景画像の上に取得済みoverlayImageを重ねる */}
        <div
          className="relative"
          style={{
            minHeight: fixedSize.height,
            height: fixedSize.height,
          }}
        >
          {sheet.backgroundImage && (
            <img
              key={`img-1-${sheet.id}`}
              src={sheet.backgroundImage}
              alt={''}
              className="absolute top-0 left-0 w-full h-full object-contain"
              onLoad={handleImageLoad}
            />
          )}
          {sheet.stamps?.map((v) => {
            if ((v.hasCount ?? 0) < 1 || !v.overlayImage) return null
            return (
              <img
                key={v.name}
                src={v.overlayImage}
                alt={v.name}
                className="absolute top-0 left-0 w-full h-full object-contain"
              />
            )
          })}
        </div>
        {/* シート名・達成状況 */}
        <div className="relative mt-4">
          <Typography variant="body1" component="h2" className="px-3 text-center">
            {sheet.name}
          </Typography>
        </div>
        <div className="mt-1 mr-3 flex items-center justify-end">
          <Typography variant="body2">
            {campaign.isRepeatStamp
              ? `${t('home.you_got', '獲得数')}： ${sheet.hasCount}${t('home.count_unit', '個')}`
              : `${t('home.achievement_count', '達成数')}： ${sheet.hasCount}/${sheet.total}`}
          </Typography>
        </div>
        {/* スタンプタイルグリッド */}
        <div className={`grid grid-cols-3 items-start justify-items-center ${styles.c_stamp_contents_wrap}`}>
          {sheet.stamps?.map((contentStamp, i: number) => {
            const stampNotAcquired = contentStamp.image.endsWith('system/non-stamp.png')
            const stampShape = stampNotAcquired ? 'circular' : campaign.stampShape === 0 ? 'circular' : 'square'
            return (
              <StampComponents
                key={`slide-${sheet.id}-stamp-cmp-${contentStamp.spotId}-${i}`}
                query={contentStamp.spotId}
                hasCount={contentStamp.hasCount ?? 0}
                course={sheet.name}
                img={contentStamp.image}
                name={contentStamp.name}
                spots={contentStamp.spots as [{ address: string; id: number; name: string; zip: string }]}
                isRepeatStamp={campaign?.isRepeatStamp}
                stampShape={stampShape}
                onGroupModal={() =>
                  onStampClick({
                    id: contentStamp.id,
                    name: contentStamp.name,
                    image: contentStamp.image,
                    point: contentStamp.point,
                    isHas: contentStamp.isHas ?? false,
                    spots: contentStamp.spots,
                  })
                }
              />
            )
          })}
        </div>
      </div>
    )
  }
}
